import React, { useEffect, useState } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Search, Download, FileText } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";

const CATEGORIES = ["contract", "agreement", "supplier_doc", "tax", "ursb", "nssf", "license", "permit", "other"];

export default function Documents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ title: "", category: "contract", description: "", expiry_date: "", file: null });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/documents");
        if (!cancelled) setItems(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const load = () => setRefreshKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error("Please choose a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      const { data: up } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/documents", {
        title: form.title, category: form.category, description: form.description,
        expiry_date: form.expiry_date || undefined, file_id: up.id,
      });
      toast.success("Document uploaded");
      setOpen(false);
      setForm({ title: "", category: "contract", description: "", expiry_date: "", file: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = items.filter((i) =>
    !q || `${i.title} ${i.category} ${i.description}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        eyebrow="Communications"
        title="Document Repository"
        subtitle="Contracts, licenses, URSB/NSSF documents, and permits — versioned and secure."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-950 text-white" data-testid="document-new-button">
                <Plus size={16} className="mr-1.5" /> Upload document
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Upload document</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="document-form">
                <div>
                  <Label>Title</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1" data-testid="document-title-input" />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1" data-testid="document-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1" data-testid="document-description-input" />
                </div>
                <div>
                  <Label>Expiry date</Label>
                  <Input type="date" value={form.expiry_date}
                    onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                    className="mt-1" data-testid="document-expiry-input" />
                </div>
                <div>
                  <Label>File</Label>
                  <Input type="file" required onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
                    className="mt-1" data-testid="document-file-input" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="document-submit-button">
                    {busy ? "Uploading…" : "Upload"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search documents…" value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9" data-testid="document-search-input" />
        </div>
        <span className="text-xs text-gray-500">{filtered.length} document(s)</span>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No documents yet" hint="Upload your first contract or license." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-grid">
          {filtered.map((d) => (
            <div key={d.id} className="bg-white border border-gray-200 rounded-md p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-900 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                  <FileText size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{d.title}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge tone="emerald">{d.category?.replaceAll("_", " ")}</Badge>
                    {d.expiry_date && <span className="text-xs text-gray-500">exp {d.expiry_date}</span>}
                  </div>
                  {d.description && <div className="text-xs text-gray-600 mt-2 line-clamp-2">{d.description}</div>}
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer"
                  className="flex-1 text-xs inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border border-emerald-900 text-emerald-900 hover:bg-emerald-50">
                  <Download size={12} /> Download
                </a>
                <Button variant="ghost" size="sm" onClick={() => remove(d.id)}
                  className="text-red-700 hover:bg-red-50" data-testid={`document-delete-${d.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
